import { MaterialIcons } from "@expo/vector-icons";
import * as Haptics from "expo-haptics";
import { LinearGradient } from "expo-linear-gradient";
import React, { useRef } from "react";
import {
  Animated,
  Pressable,
  Text,
  StyleSheet,
  View,
  Platform,
  ViewStyle,
  TextStyle,
  GestureResponderEvent
} from "react-native";

import { useAppTheme } from "../../design-system/ThemeProvider";
import { spacing, radius, shadows } from "../../design-system/tokens";


type PremiumButtonProps = {
  text: string;
  onPress: (event: GestureResponderEvent) => void;
  iconName?: string;
  iconPosition?: "left" | "right";
  disabled?: boolean;
  gradientColors?: string[];
  style?: ViewStyle;
  textStyle?: TextStyle;
  accessibilityLabel?: string;
};

const PremiumButton: React.FC<PremiumButtonProps> = ({
  text,
  onPress,
  iconName,
  iconPosition = "right",
  disabled = false,
  gradientColors,
  style,
  textStyle,
  accessibilityLabel
}) => {
  const { colors } = useAppTheme();
  const styles = createStyles(colors);
  const scaleAnim = useRef(new Animated.Value(1)).current;

  const handlePressIn = () => {
    Animated.spring(scaleAnim, {
      toValue: 0.96,
      useNativeDriver: true,
      speed: 40,
      bounciness: 8
    }).start();
  };

  const handlePressOut = () => {
    Animated.spring(scaleAnim, {
      toValue: 1,
      useNativeDriver: true,
      speed: 40,
      bounciness: 8
    }).start();
  };

  const handlePress = (event: GestureResponderEvent) => {
    if (!disabled && onPress) {
      // Only trigger haptics on native platforms
      if (Platform.OS !== 'web') {
        try {
          Haptics.selectionAsync();
        } catch (error) {
          // Silently handle haptics errors
          console.log('[Haptics] Not available on this platform');
        }
      }
      onPress(event);
    }
  };

  return (
    <Pressable
      onPressIn={handlePressIn}
      onPressOut={handlePressOut}
      onPress={handlePress}
      accessibilityRole="button"
      accessibilityLabel={accessibilityLabel || text}
      disabled={disabled}
    >
      <Animated.View
        style={[
          styles.animatedButton,
          { transform: [{ scale: scaleAnim }] },
          disabled && styles.buttonDisabled,
          style
        ]}
      >
        <LinearGradient
          colors={
            disabled
              ? [colors.outlineVariant, colors.outlineVariant] as const
              : (gradientColors || [colors.primary, colors.primaryDark]) as [string, string]
          }
          start={{ x: 0, y: 0 }}
          end={{ x: 1, y: 1 }}
          style={styles.gradientBg}
        >
          <View style={styles.contentRow}>
            {iconName && iconPosition === "left" && (
              <MaterialIcons
                name={iconName as any}
                size={22}
                color="#fff"
                style={styles.icon}
              />
            )}
            <Text style={[styles.text, textStyle]}>{text}</Text>
            {iconName && iconPosition === "right" && (
              <MaterialIcons
                name={iconName as any}
                size={22}
                color="#fff"
                style={styles.icon}
              />
            )}
          </View>
        </LinearGradient>
      </Animated.View>
    </Pressable>
  );
};

const createStyles = (colors: any) => StyleSheet.create({
  animatedButton: {
    borderRadius: radius.xl,
    marginTop: spacing.lg,
    ...Platform.select({
      ios: {
        shadowColor: colors.primary,
        shadowOffset: { width: 0, height: 4 },
        shadowOpacity: 0.25,
        shadowRadius: 12,
      },
      android: {
        elevation: 8,
      },
      web: {
        boxShadow: `0px 4px 24px 0px ${colors.primary}40`,
      },
    }),
  },
  gradientBg: {
    borderRadius: radius.xl,
    paddingVertical: spacing.lg,
    paddingHorizontal: spacing['3xl'],
    justifyContent: "center",
    alignItems: "center"
  },
  buttonDisabled: {
    opacity: 0.65
  },
  contentRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center"
  },
  text: {
    color: colors.onPrimary,
    fontSize: 16,
    fontWeight: "700",
    letterSpacing: 0.5,
    marginHorizontal: spacing.sm,
  },
  icon: {
    marginHorizontal: 4
  }
});

export default PremiumButton;
